import { ContentLoading } from '@/components/common/content-loading';
import { AlertIcon, LongRightArrowIcon, PlaceIcon, ShopIcon } from '@/components/icons/event';
import { type EventBenefitCategoryCode, type EventCategoryIconId } from '@/constants/event-categories';
import { useTickSoundInteraction } from '@/hooks';
import { useOffEvtListQuery } from '@/services/queries/event';
import { useConfigStore } from '@/stores';
import { getAvailableEventCategories, mapDutyFreeEvents } from '@/utils/event';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface EventCategoryViewProps {
    onSelectCategory: (categoryCode: EventBenefitCategoryCode) => void;
}

const CATEGORY_ICONS: Record<EventCategoryIconId, typeof PlaceIcon> = {
    place: PlaceIcon,
    shop: ShopIcon,
};

export default function EventCategoryView({ onSelectCategory }: EventCategoryViewProps) {
    const { t } = useTranslation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();
    const language = useConfigStore((state) => state.config.ui.language);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const isHalfScreen = screenMode === 'split';
    const storeCode = useConfigStore((state) => state.config.server?.store?.storeCode ?? null);

    const { data, isLoading, isError } = useOffEvtListQuery();

    const availableCategories = useMemo(() => {
        const mapped = mapDutyFreeEvents(data);
        return getAvailableEventCategories(mapped, language, storeCode);
    }, [data, language, storeCode]);

    if (isLoading) {
        return (
            <main className={clsx(styles.main, isHalfScreen && styles['main--half'])} data-nav="content">
                <ContentLoading loading />
            </main>
        );
    }

    if (isError) {
        return (
            <main className={clsx(styles.main, isHalfScreen && styles['main--half'])} data-nav="content">
                <p className={styles.empty}>{t('event.load_error')}</p>
            </main>
        );
    }

    if (availableCategories.length === 0) {
        return (
            <main className={clsx(styles.main, isHalfScreen && styles['main--half'])} data-nav="content">
                <div className={styles.empty} data-region-nav data-region-order={2} tabIndex={2} data-interactive aria-label={t('event.no_events')}>
                    <AlertIcon className={styles.empty__icon} aria-hidden />
                    <p className={styles.empty__text}>{t('event.no_events')}</p>
                </div>
            </main>
        );
    }

    return (
        <main className={clsx(styles.main, isHalfScreen && styles['main--half'])} data-nav="content">
            <div className={clsx(styles.list, isHalfScreen && styles['list--half'])} data-region-nav data-region-order={2}>
                {availableCategories.map((category) => {
                    const title = t(category.titleKey);
                    const subtitle = t(category.subtitleKey);
                    const cta = t(category.ctaKey);
                    const Icon = CATEGORY_ICONS[category.icon];

                    return (
                        <div
                            key={category.id}
                            role="button"
                            tabIndex={2}
                            data-interactive
                            data-type="button"
                            data-role={t('common.button.tts.button')}
                            aria-label={title}
                            className={clsx(styles.card, 'focus-border', isHalfScreen && styles['card--half'])}
                            onClick={(e) => handleInteraction(e, () => onSelectCategory(category.code))}
                            onKeyDown={(e) => handleKeyboardInteraction(e, () => onSelectCategory(category.code))}
                        >
                            <div className={styles.card__body}>
                                <div className={styles.card__text}>
                                    <p className={styles.card__subtitle}>{subtitle}</p>
                                    <p className={styles.card__title}>{title}</p>
                                    <div className={styles.card__cta}>
                                        <span className={styles.card__ctaText}>{cta}</span>
                                        <LongRightArrowIcon
                                            className={styles.card__ctaArrow}
                                            color="var(--color-text-sub01)"
                                            width="calc(87 * var(--vw-unit))"
                                            height="calc(22 * var(--vh-unit))"
                                            aria-hidden
                                        />
                                    </div>
                                </div>
                                <div className={styles.card__icon} aria-hidden>
                                    <Icon width="100%" height="100%" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </main>
    );
}
